import { Box } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const MotionBox = motion.create(Box);

/** Thickness of the progress line, in px. */
const BAR_HEIGHT = 3;

/**
 * Page-change progress line along the top edge of the content. Rendered as the
 * first child of the scrolling content panel, it inherits that panel's place
 * and its rounded corner, and sticks to its top while the page scrolls.
 *
 * A neon line: lilac to rose, the Companion spark's own ramp, haloed so it reads
 * at a glance on the white page, with a bright spark riding its leading edge.
 */
const Loader: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Reset and start loading
    setProgress(0);
    setIsLoading(true);

    // Simulate progress
    let currentProgress = 0;
    intervalRef.current = setInterval(() => {
      currentProgress += Math.random() * 30 + 10;
      if (currentProgress >= 90) {
        currentProgress = 90;
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
      setProgress(currentProgress);
    }, 100);

    // Complete after a short delay
    const completeTimer = setTimeout(() => {
      setProgress(100);
      setTimeout(() => {
        setIsLoading(false);
      }, 200);
    }, 600);

    // Cleanup
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      clearTimeout(completeTimer);
    };
  }, [location]);

  return (
    // Zero height, so it takes no room above the page it loads.
    <Box position="sticky" top={0} h={0} zIndex="banner">
      <AnimatePresence>
        {isLoading && (
          <motion.div
            role="progressbar"
            aria-label={t('navigation.pageLoading')}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: `${BAR_HEIGHT}px`,
              pointerEvents: 'none',
            }}
          >
            <MotionBox
              position="relative"
              h="full"
              roundedRight="full"
              bgImage="linear-gradient(90deg, {colors.lilac.600}, {colors.lilac.400} 65%, {colors.rose.300})"
              boxShadow="loader.neon"
              _after={{
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                h: 'full',
                w: '28px',
                rounded: 'full',
                bgImage: 'linear-gradient(90deg, transparent, white)',
                boxShadow: 'loader.spark',
              }}
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Loader;
